import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PMPagination } from "../../../utils/PMPagination";
import { Link, useNavigate } from "react-router-dom";
import { CopyToClipboard } from "../../../utils/Div";
import { ResourceEmpty } from "../../../utils/ResourceEmpty";
import {
  PencilIcon,
  UserIcon,
  CalendarIcon,
  PhoneIcon,
  EnvelopeIcon,
  Cog6ToothIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";

export const UsersTable = (props) => {
  const { t } = useTranslation();
  const {
    itemState,
    onPageChange,
    itemsCount,
    currentCount,
    page,
    perPage,
    onItemsSelect,
  } = props;
  const { data, isLoading, errors } = itemState;
  const users = data;
  const checkbox = useRef();
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  useLayoutEffect(() => {
    const isIndeterminate =
      selectedItems.length > 0 && selectedItems.length < users?.length;
    setChecked(selectedItems.length === users?.length);
    setIndeterminate(isIndeterminate);
    if (checkbox.current) {
      checkbox.current.indeterminate = isIndeterminate;
    }
    onItemsSelect(selectedItems);
  }, [selectedItems, users?.length, onItemsSelect]);

  function toggleAll() {
    setSelectedItems(checked || indeterminate ? [] : users || []);
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  }

  const navigate = useNavigate();

  const getUserStatus = (user) => {
    if (!user.active)
      return {
        label: t("company.users.table.status_inactive"),
        class: "bg-red-50 text-red-800 ring-1 ring-red-100",
      };
    if (!user.approved)
      return {
        label: t("company.users.table.status_pending"),
        class: "bg-amber-50 text-amber-900 ring-1 ring-amber-100",
      };
    return {
      label: t("company.users.table.status_active"),
      class: "bg-emerald-50 text-emerald-900 ring-1 ring-emerald-100",
    };
  };

  const getUserRoles = (user) => {
    if (!user.roles || user.roles.length === 0) return [];
    return user.roles.map((role) => role.name);
  };

  const secondaryBtnClass =
    "inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50";
  const primaryBtnClass =
    "inline-flex items-center gap-1.5 rounded-full bg-slate-950 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-800";

  function renderUserRows() {
    if (!users || users.length === 0) {
      return (
        <div className="px-6 py-12">
          <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm">
              <UserIcon className="h-7 w-7" />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-slate-900">
              {t("company.users.table.empty_title")}
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              {t("company.users.table.empty_hint")}
            </p>
          </div>
        </div>
      );
    }

    return users.map((user) => {
      const status = getUserStatus(user);
      const roles = getUserRoles(user);
      const selected = selectedItems.includes(user);

      return (
        <div
          key={user.id}
          className={`px-6 py-5 transition hover:bg-slate-50 ${
            selected ? "bg-slate-50/80 ring-1 ring-inset ring-slate-200" : ""
          }`}
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
            <div className="flex min-w-0 flex-shrink-0 items-start gap-3">
              <input
                type="checkbox"
                className="mt-3 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                value={user.id}
                checked={selected}
                onChange={(e) =>
                  setSelectedItems(
                    e.target.checked
                      ? [...selectedItems, user]
                      : selectedItems.filter((p) => p !== user)
                  )
                }
              />
              <Link
                to={"/dashboard/users/" + user?.id}
                className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200"
              >
                <UserIcon className="h-6 w-6" />
              </Link>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {t("company.users.table.row_kicker")}
                </span>
                <span
                  className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${status.class}`}
                >
                  {status.label}
                </span>
              </div>

              <div className="mt-2">
                <Link
                  to={"/dashboard/users/" + user?.id}
                  className="text-lg font-semibold text-slate-950 transition hover:text-slate-700"
                >
                  {user?.name || t("company.users.table.unnamed")}
                </Link>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                <div className="flex min-w-0 items-center gap-1.5">
                  <EnvelopeIcon className="h-4 w-4 flex-shrink-0 text-slate-400" />
                  <Link
                    to={"mailto:" + user?.email}
                    className="truncate font-medium text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-500"
                  >
                    {user?.email}
                  </Link>
                </div>
                {user?.mobile ? (
                  <div className="flex items-center gap-1.5">
                    <PhoneIcon className="h-4 w-4 flex-shrink-0 text-slate-400" />
                    <CopyToClipboard path={null} content={user?.mobile} type="popup" />
                  </div>
                ) : null}
              </div>

              {roles.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {roles.slice(0, 6).map((role, index) => (
                    <span
                      key={index}
                      className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600"
                    >
                      {role}
                    </span>
                  ))}
                  {roles.length > 6 ? (
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                      +{roles.length - 6}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="flex w-full flex-col gap-3 lg:w-52 lg:items-stretch">
              <div className="flex flex-wrap gap-2 lg:justify-end">
                <button
                  type="button"
                  onClick={() => {
                    const today = new Date().toISOString().split("T")[0];
                    window.open(
                      `/dashboard/calendar?date=${today}&view=day&users=${user.id}&showWeekNumbers=false`,
                      "_blank"
                    );
                  }}
                  className={secondaryBtnClass}
                >
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {t("company.users.table.calendar")}
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/users/" + user.id)}
                  className={primaryBtnClass}
                >
                  <PencilIcon className="h-3.5 w-3.5" />
                  {t("company.users.table.edit")}
                </button>
              </div>
              <div className="flex flex-wrap gap-2 lg:justify-end">
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/users/" + user.id)}
                  className={secondaryBtnClass}
                >
                  <Cog6ToothIcon className="h-3.5 w-3.5" />
                  {t("company.users.table.settings")}
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/users/" + user.id)}
                  className={secondaryBtnClass}
                >
                  <EyeIcon className="h-3.5 w-3.5" />
                  {t("company.users.table.view")}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    });
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 px-6 py-16">
        <div
          className="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-slate-600"
          aria-hidden
        />
        <p className="text-sm text-slate-500">{t("company.users.loading")}</p>
      </div>
    );
  }

  return (
    <div>
      {users && users.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50/60 px-6 py-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
              ref={checkbox}
              checked={checked}
              onChange={toggleAll}
            />
            <span className="text-sm text-slate-600">
              {selectedItems.length > 0
                ? t("company.users.table.selected_of_total", {
                    selected: selectedItems.length,
                    total: users.length,
                  })
                : t("company.users.table.user_count", { count: users.length })}
            </span>
          </div>
          {selectedItems.length > 0 ? (
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              {t("company.users.table.n_selected", {
                count: selectedItems.length,
              })}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="divide-y divide-slate-200">{renderUserRows()}</div>

      {Array.isArray(errors) && errors.length > 0 ? (
        <div className="border-t border-slate-200 px-6 py-4 text-sm text-red-600">
          {errors.join(" ")}
        </div>
      ) : null}

      <div className="border-t border-slate-200 px-6 py-5">
        <PMPagination
          disabled={isLoading}
          onPageChange={onPageChange}
          page={page}
          perPage={perPage}
          totalCount={itemsCount}
          currentCount={currentCount}
          showCount={true}
        />
      </div>
    </div>
  );
};

UsersTable.defaultProps = {
  page: 1,
  itemState: [],
  itemsCount: 0,
  selectedItems: [],
  sort: "desc",
  sortBy: "createdAt",
};
